import { BarChart3, Map, Target, RefreshCw, type LucideIcon } from 'lucide-react';
import { Card, Badge, Button, SectionLabel, Callout, Stat } from '@/shared/ui';
import type { LifecycleData, StrategyMeta } from '../api/lifecycleApi';
import type { InsightsResult, OverviewStats, StrategyItem } from '../api/strategyApi';
import styles from './StrategyOverviewPage.module.css';
import patterns from '@/shared/ui/styles/patterns.module.css';

function formatPopulation(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'Unknown';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Updated today';
  if (days === 1) return 'Updated yesterday';
  if (days < 30) return `Updated ${days} days ago`;
  if (days < 365) return `Updated ${Math.floor(days / 30)} months ago`;
  return `Updated ${Math.floor(days / 365)}y ago`;
}

function addMonths(iso: string, months: number): Date {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d;
}

function bestRevenueImpact(strategies: StrategyItem[]): string {
  const tiers = ['Exceptional', 'Very Good', 'Good'];
  let best = 2;
  for (const s of strategies) {
    const idx = tiers.indexOf(s.revenue_impact ?? 'Good');
    if (idx >= 0 && idx < best) best = idx;
  }
  return strategies.length > 0 ? tiers[best] : '—';
}

const QUICK_LINKS: Array<{
  label: string;
  desc: (o: OverviewStats | null, s: StrategyItem[]) => string;
  step: number;
  icon: LucideIcon;
}> = [
  { label: 'Market Intelligence', desc: () => 'Demographics & gaps', step: 3, icon: BarChart3 },
  {
    label: 'Competition',
    desc: (o) => (o?.competitorCount ? `${o.competitorCount} practices analyzed` : 'View analysis'),
    step: 4,
    icon: Map,
  },
  {
    label: 'Strategy & Insights',
    desc: (_, s) => `${s.length} active strategies`,
    step: 5,
    icon: Target,
  },
];

interface Props {
  lifecycle: LifecycleData;
  insights: InsightsResult | null;
  overview: OverviewStats | null;
  draft?: StrategyMeta | null;
  onResumeDraft?: () => void;
  onDiscardDraft?: () => void;
  discardingDraft?: boolean;
  onEdit: () => void;
  /** Opening a refresh draft for Edit Strategy. */
  editing?: boolean;
  onNavigateStep: (step: number) => void;
}

export function ActiveView({
  lifecycle,
  insights,
  overview,
  draft,
  onResumeDraft,
  onDiscardDraft,
  discardingDraft,
  onEdit,
  editing,
  onNavigateStep,
}: Props) {
  const approved = new Set(insights?.approvedIndexes ?? []);
  const strats = (insights?.strategies ?? []).filter((_, i) => approved.has(i));
  const topRevenueImpact = bestRevenueImpact(strats);
  const { refreshWarning, enrichment, active } = lifecycle;
  const daysSince = refreshWarning?.daysSinceCompletion ?? null;
  const tooEarly = daysSince !== null && daysSince < 60;

  const freshnessItems = [
    {
      label: 'Practice Data',
      status: enrichment?.uploadCount
        ? `${enrichment.uploadCount} file${enrichment.uploadCount > 1 ? 's' : ''} uploaded (${enrichment.level})`
        : 'No data uploaded',
      ok: !!enrichment?.uploadCount,
    },
    {
      label: 'Census / Market',
      status: overview?.censusUpdatedAt ? timeAgo(overview.censusUpdatedAt) : 'Not loaded',
      ok: !!overview?.censusUpdatedAt,
    },
    {
      label: 'Competition',
      status: overview?.competitorUpdatedAt ? timeAgo(overview.competitorUpdatedAt) : 'Not loaded',
      ok: !!overview?.competitorUpdatedAt,
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.strategyHeader}>
        <div>
          <h2 className={styles.pageTitle}>Practice Strategy</h2>
          <div className={styles.badgeRow}>
            <Badge variant="primary">Active</Badge>
            {active?.completedAt && (
              <>
                <span className={styles.dateMeta}>
                  Last updated: {new Date(active.completedAt).toLocaleDateString()}
                </span>
                <span className={styles.dateDot}>·</span>
                <span className={styles.dateMeta}>
                  Next recommended refresh: {addMonths(active.completedAt, 6).toLocaleDateString()}
                </span>
              </>
            )}
          </div>
        </div>
        <Button size="sm" variant="secondary" onClick={onEdit} loading={editing} disabled={editing}>
          Edit Strategy
        </Button>
      </div>

      {draft && (
        <Callout
          variant="warning"
          icon={RefreshCw}
          title="Strategy refresh in progress"
          actions={
            <>
              <Button size="sm" variant="primary" onClick={onResumeDraft}>
                Resume →
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={onDiscardDraft}
                disabled={discardingDraft}
              >
                {discardingDraft ? 'Discarding…' : 'Discard'}
              </Button>
            </>
          }
          className={styles.calloutSpaced}
        >
          Version {draft.version} is in draft (step {draft.currentStep ?? 1}). Your active strategy
          stays live until the refresh is approved.
        </Callout>
      )}

      {tooEarly && (
        <Callout
          variant="warning"
          title={`Strategy is ${daysSince} days old — refresh recommended every 6 months`}
          className={styles.calloutSpaced}
        >
          Major changes in less than 2–3 months may not give campaigns enough time to optimize.
        </Callout>
      )}

      {refreshWarning?.type === 'due_for_refresh' && (
        <Callout
          variant="accent"
          icon={RefreshCw}
          title="Time for a refresh!"
          className={styles.calloutSpaced}
        >
          {refreshWarning.message}
        </Callout>
      )}

      <Card className={styles.cardMbSm}>
        <div className={styles.statsGrid4}>
          <Stat
            label="Market Population"
            value={formatPopulation(overview?.marketPopulation ?? null)}
            sub={overview ? `${overview.zipCount} zip codes` : '—'}
          />
          <Stat
            label="Market Share"
            value={overview?.marketSharePercent != null ? `${overview.marketSharePercent}%` : '—'}
            sub={
              overview?.totalPatients != null
                ? `${overview.totalPatients.toLocaleString()} patients`
                : '—'
            }
            tone="warning"
          />
          <Stat label="Active Strategies" value={String(strats.length || 0)} tone="accent" />
          <Stat
            label="Revenue Impact"
            value={topRevenueImpact}
            tone={
              topRevenueImpact === 'Exceptional'
                ? 'accent'
                : topRevenueImpact === 'Very Good'
                  ? 'info'
                  : 'warning'
            }
          />
        </div>
      </Card>

      <div className={styles.quickLinksGrid}>
        {QUICK_LINKS.map((x, i) => {
          const Icon = x.icon;
          return (
            <div
              key={i}
              onClick={() => onNavigateStep(x.step)}
              className={styles.quickLinkCard}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onNavigateStep(x.step)}
            >
              <div className={styles.quickLinkIcon}>
                <Icon size={22} strokeWidth={1.75} aria-hidden />
              </div>
              <div className={styles.quickLinkLabel}>{x.label}</div>
              <div className={styles.quickLinkDesc}>{x.desc(overview, strats)}</div>
            </div>
          );
        })}
      </div>

      <SectionLabel label="Active Strategies" sub="Powering your marketing execution" />
      {strats.map((s: StrategyItem, i: number) => (
        <Card key={i} className={styles.cardMbXs}>
          <div className={styles.strategyRow}>
            <div>
              <div className={styles.strategyLabelGroup}>
                <span className={styles.strategyTitle}>{s.title}</span>
                <Badge variant={s.type === 'OPPORTUNITY' ? 'primary' : 'warning'}>{s.type}</Badge>
              </div>
              <div className={styles.channelPills}>
                {(s.primary_channels ?? []).slice(0, 3).map((ch: string, j: number) => (
                  <span key={j} className={patterns.pillMuted}>
                    {ch.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
            {s.revenue_impact && (
              <Badge
                variant={
                  s.revenue_impact === 'Exceptional'
                    ? 'primary'
                    : s.revenue_impact === 'Very Good'
                      ? 'info'
                      : 'warning'
                }
              >
                {s.revenue_impact}
              </Badge>
            )}
          </div>
        </Card>
      ))}

      <Card className={styles.cardMtSm}>
        <SectionLabel label="Data Sources & Freshness" />
        <div className={styles.freshnessGrid}>
          {freshnessItems.map((d, i) => (
            <div key={i} className={styles.freshnessCell}>
              <div className={styles.freshnessLabel}>{d.label}</div>
              <div
                className={[
                  styles.freshnessStatus,
                  d.ok ? styles.freshnessOk : styles.freshnessWarning,
                ].join(' ')}
              >
                {d.status}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
